import {
  ArrowSquareOut,
  Briefcase,
  CreditCard,
  Eye,
  FlagBannerFold,
  GearFine,
  GlobeHemisphereWest,
  Layout,
  Palette,
  PlusCircle,
  Receipt,
  Tag,
  User,
  UserCircleDashed,
  UserMinus,
  Users,
  WebhooksLogo,
} from "@phosphor-icons/react";

import InviteMemberModal from "common/components/InviteMemberModal";
import { Button } from "common/components/ui/Button";
import WorkspaceSelector from "common/components/WorkspaceSelector";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import usePromotions from "common/datahooks/usePromotions";
import useWorkspaceToken from "common/datahooks/useWorkspaceToken";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import useWhitelabelCustomization from "common/hooks/useWhitelabelCustomization";
import type { UserPermissions } from "common/types";

import AgencyInvoices from "../components/AgencyPlan/AgencyInvoices";
import GetAgencyPlan from "../components/AgencyPlan/GetAgencyPlan";
import PlanDetails from "../components/AgencyPlan/PlanDetails";
import ApiKeys from "../components/ApiKeys";
import CreateApiTokenButton from "../components/ApiKeys/CreateApiTokenButton";
import Billing from "../components/Billing";
import Blacklist from "../components/Blacklist";
import AddToBlacklistButton from "../components/Blacklist/AddToBlacklistButton/AddToBlacklistButton";
import Clients from "../components/Clients";
import NewClientButton from "../components/Clients/NewClientButton";
import General from "../components/General";
import Labels from "../components/Labels";
import AddLabelButton from "../components/Labels/AddLabelButton";
import LifetimeSeats from "../components/LifetimeSeats";
import Members from "../components/Members";
import NonAgencyInvoices from "../components/NonAgencyInvoices";
import ProfileSettings from "../components/ProfileSettings";
import Proxies from "../components/Proxies";
import AddProxyButton from "../components/Proxies/AddProxyButton";
import Webhooks from "../components/Webhooks";
import NewWebhookButton from "../components/Webhooks/NewWebhookButton";
import Whitelabel from "../components/Whitelabel";
import { WHITELABEL_DOMAIN } from "../constants";
import useAgencySettings from "../datahooks/useAgencySettings";
import useWhitelabelPreviewUrl from "./useWhitelabelPreviewUrl";

import type { JSX, ReactNode } from "react";

interface NavItem {
  path: string;
  label: string;
  icon: ReactNode;
  component: JSX.Element;
  actionComponent?: JSX.Element;
  visibleForPermissions?: UserPermissions;
}
interface NavItemWithSubNav extends Omit<NavItem, "component"> {
  subNavItems: Omit<NavItem, "icon" | "visibleForPermissions">[];
}
interface NavItemComponent {
  element: ReactNode;
}

export default function useSettingsNavItems() {
  const isDesktop = useTwBreakpoint("lg");

  const { permissions, role } = useSelectedWorkspaceContext();
  const { workspaceToken } = useWorkspaceToken();
  const { agencyPlan, appSumoPromotion, ltdPromotion } = usePromotions();
  const { agencySettings } = useAgencySettings();
  const { whitelabelCustomization } = useWhitelabelCustomization();
  const { whitelabelPreviewUrl, refreshWhitelabelPreviewUrl } =
    useWhitelabelPreviewUrl(
      agencySettings ? `${agencySettings.slug}.${WHITELABEL_DOMAIN}` : "",
    );

  const actionButtonSize = isDesktop ? "sm" : "md";

  const navItems: (NavItem | NavItemWithSubNav | NavItemComponent)[] = [
    {
      path: "",
      label: "Profile settings",
      icon: <User weight="fill" />,
      component: <ProfileSettings />,
    },
  ];
  if (!whitelabelCustomization) {
    if (isDesktop) {
      navItems.unshift({ element: <WorkspaceSelector isExpanded /> });
    }
    navItems.push(
      {
        path: "general",
        label: "General",
        icon: <Layout weight="fill" />,
        component: <General />,
      },
      {
        path: "billing",
        label: "Billing",
        icon: <CreditCard weight="fill" />,
        component: <Billing />,
      },
      {
        path: "invoices",
        label: "Invoices",
        icon: <Receipt weight="fill" />,
        component: <NonAgencyInvoices />,
        visibleForPermissions: ["billing_full", "billing_limited"],
      },
      {
        path: "members",
        label: "Members",
        icon: <Users weight="fill" />,
        component: <Members />,
        visibleForPermissions: ["members_full", "members_limited"],
        actionComponent: (
          <InviteMemberModal
            buttonComponent={
              <Button
                leftIcon={<PlusCircle weight="fill" />}
                size={actionButtonSize}
              >
                Invite teammate
              </Button>
            }
          />
        ),
      },
    );
    if (agencyPlan && role === "owner") {
      navItems.push({
        path: "clients",
        label: "Clients",
        icon: <UserCircleDashed weight="fill" />,
        component: <Clients />,
        actionComponent: <NewClientButton size={actionButtonSize} />,
      });
    }
  }
  navItems.push({
    path: "labels",
    label: "Labels",
    icon: <Tag weight="fill" />,
    visibleForPermissions: ["leads_full"],
    subNavItems: [
      // {
      //   path: "campaigns",
      //   label: "Campaigns",
      //   component: <Labels type="campaign" />,
      //   actionComponent: <AddLabelButton labelType="campaign" />,
      // },
      // {
      //   path: "accounts",
      //   label: "Accounts",
      //   component: <Labels type="account" />,
      //   actionComponent: <AddLabelButton labelType="account" />,
      // },
      {
        path: "leads",
        label: "Leads",
        component: <Labels type="workspace" />,
        actionComponent: (
          <AddLabelButton labelType="workspace" size={actionButtonSize} />
        ),
      },
    ],
  });

  if (!whitelabelCustomization) {
    navItems.push(
      {
        path: "api",
        label: "API keys",
        icon: <GearFine weight="fill" />,
        component: <ApiKeys />,
        visibleForPermissions: ["integrations_full"],
        actionComponent: (
          <div className="flex gap-x-2.5">
            <Button
              variant="secondary-black"
              className="flex-1"
              asChild
              leftIcon={<ArrowSquareOut />}
              size={actionButtonSize}
            >
              <a
                href="https://docs.aimfox.com/"
                target="_blank"
                rel="noreferrer"
              >
                Documentation
              </a>
            </Button>
            <CreateApiTokenButton size={actionButtonSize} />
          </div>
        ),
      },
      {
        path: "webhooks",
        label: "Webhooks",
        icon: <WebhooksLogo weight="fill" />,
        component: <Webhooks />,
        visibleForPermissions: ["webhooks_full"],
        actionComponent: (
          <div className="flex gap-x-2.5">
            <Button
              variant="secondary-black"
              leftIcon={<ArrowSquareOut />}
              size={actionButtonSize}
              asChild
            >
              <a
                href="https://docs.aimfox.com/webhooks"
                target="_blank"
                rel="noreferrer"
              >
                Documentation
              </a>
            </Button>
            <NewWebhookButton size={actionButtonSize} />
          </div>
        ),
      },
    );
  }

  navItems.push(
    {
      path: "blacklist",
      label: "Blacklist",
      icon: <UserMinus weight="fill" />,
      visibleForPermissions: ["campaigns_full"],
      subNavItems: [
        {
          path: "accounts",
          label: "Accounts",
          component: <Blacklist type="accounts" />,
          actionComponent: (
            <AddToBlacklistButton type="accounts" size={actionButtonSize} />
          ),
        },
        {
          path: "companies",
          label: "Companies",
          component: <Blacklist type="companies" />,
          actionComponent: (
            <AddToBlacklistButton type="companies" size={actionButtonSize} />
          ),
        },
      ],
    },
    {
      path: "proxies",
      label: "Proxies",
      icon: <GlobeHemisphereWest weight="fill" />,
      component: <Proxies />,
      visibleForPermissions: ["accounts_full"],
      actionComponent: (
        <div className="flex gap-x-2.5">
          <Button
            variant="secondary-black"
            leftIcon={<ArrowSquareOut />}
            asChild
            size={actionButtonSize}
            className="max-lg:flex-1"
          >
            <a href="https://docs.aimfox.com/" target="_blank" rel="noreferrer">
              Documentation
            </a>
          </Button>
          <AddProxyButton size={actionButtonSize} />
        </div>
      ),
    },
  );

  if (!whitelabelCustomization) {
    if (role === "owner") {
      navItems.push({
        element: <div className="lg:w-full lg:h-px bg-black-200 mb-1 mt-2" />,
      });
      if (agencyPlan) {
        navItems.push({
          path: "agency",
          label: "Agency plan",
          icon: <Briefcase weight="fill" />,
          subNavItems: !!agencyPlan && [
            {
              path: "manage",
              label: "Manage",
              component: <PlanDetails />,
              actionComponent: <div />,
            },
            {
              path: "invoices",
              label: "Invoices",
              component: <AgencyInvoices />,
              actionComponent: <div />,
            },
          ],
        });
        navItems.push({
          path: "whitelabel",
          label: "White label",
          icon: <Palette weight="fill" />,
          component: <Whitelabel />,
          actionComponent: agencySettings && (
            <div className="flex gap-x-2.5">
              <Button
                variant="outline-black"
                leftIcon={<Eye />}
                asChild
                size={actionButtonSize}
              >
                <a
                  target="_blank"
                  href={whitelabelPreviewUrl}
                  rel="noreferrer"
                  onClick={refreshWhitelabelPreviewUrl}
                >
                  Preview white label
                </a>
              </Button>
              <Button
                variant="outline-black"
                leftIcon={<Eye />}
                size={actionButtonSize}
                asChild
              >
                <a
                  target="_blank"
                  href={`${import.meta.env.VITE_WHITE_LABEL_URL}${workspaceToken ? `#${workspaceToken}` : ""}`}
                  rel="noreferrer"
                >
                  Preview login
                </a>
              </Button>
            </div>
          ),
        });
      } else {
        navItems.push({
          path: "agency",
          label: "Agency plan",
          icon: <Briefcase weight="fill" />,
          component: <GetAgencyPlan />,
        });
      }
    }
    if (role === "owner" && (ltdPromotion || appSumoPromotion)) {
      navItems.push({
        path: "ltd",
        label: "Lifetime seats",
        icon: <FlagBannerFold weight="fill" />,
        component: <LifetimeSeats />,
      });
    }
  }

  //filter items based on permissions
  const routes = navItems.filter((item) => {
    if ("element" in item) {
      return false;
    }
    const { visibleForPermissions } = item;
    return visibleForPermissions
      ? visibleForPermissions.some((permission) =>
          permissions.includes(permission),
        )
      : true;
  }) as (NavItem | NavItemWithSubNav)[];

  return { navItems, routes };
}
